import { useContext, useEffect, useMemo } from 'react'

import { fetchFlights } from 'actions/FlightActions'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import CurrencyContext from 'contexts/currencyContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'
import { hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { isRoomsInvalid } from 'lib/search/searchUtils'

import {
  getPrefetchFlightDestinationAirportCode,
  getPrefetchFlightReturnDate,
  getPrefetchFlightsFromOfferSearchParams,
  isOfferBundledWithFlightsOnly,
} from './prefetchFlightsFromOfferUtils'

interface Options {
  offer: App.HotelOffer | App.BedbankOffer
  offerPageState: OfferPageState
  flightsEnabled?: boolean
  forceBundled?: boolean
}

export default function usePrefetchFlightsFromOffer(options: Options) {
  const {
    offer,
    offerPageState,
    flightsEnabled = true,
    forceBundled = false,
  } = options
  const {
    activeFilter,
    activeHeaderFilter,
    activeSubFilter,
    airport,
    checkInDate,
    checkOutDate,
    duration,
    fareClass,
    rooms,
    charterBundleEnabled,
    charterBundleRoute,
  } = offerPageState

  const dispatch = useAppDispatch()
  const currentCurrency = useContext(CurrencyContext)
  const { isAnyVariant: prefetchFlights, triggerActivation } = useExperiment(Experiments.croPrefetchFlightsFromOffer, { canActivate: false })
  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)
  const bundledLuxPlusPricingEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsBundledLuxPlusPricing)
  const requestBundledLuxPlusPricing = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: bundledLuxPlusPricingEnabled,
    isBundledFlow: true,
  }))

  const destinationAirportCode = getPrefetchFlightDestinationAirportCode(offer)
  const returnDate = useMemo(
    () => getPrefetchFlightReturnDate(checkInDate, checkOutDate, duration),
    [checkInDate, checkOutDate, duration],
  )
  const shouldSearchFlights = flightsEnabled || forceBundled || isOfferBundledWithFlightsOnly(offer)
  const isCharterBundle = !!charterBundleEnabled && !!charterBundleRoute
  const isChoosingFilters = !!activeFilter || !!activeHeaderFilter || !!activeSubFilter

  useEffect(() => {
    if (
      !prefetchFlights ||
      !shouldSearchFlights ||
      isChoosingFilters ||
      isCharterBundle ||
      !airport?.code ||
      !destinationAirportCode ||
      airport.code === destinationAirportCode ||
      !checkInDate ||
      !returnDate ||
      !hasSelectedRoomsOrDefault(rooms) ||
      isRoomsInvalid(rooms)
    ) {
      return
    }

    const params = getPrefetchFlightsFromOfferSearchParams({
      offer,
      originAirportCode: airport.code,
      checkInDate,
      checkOutDate,
      duration,
      fareClass,
      rooms,
      currency: currentCurrency,
      requestBundledLuxPlusPricing,
    })

    if (!params) return
    dispatch(fetchFlights(params, {
      offerId: offer.id,
      enableSabreContent,
      enableLuxPlusPricing: bundledLuxPlusPricingEnabled,
      isBundledFlow: true,
    }))

    triggerActivation()
  }, [
    airport?.code,
    bundledLuxPlusPricingEnabled,
    checkInDate,
    checkOutDate,
    currentCurrency,
    destinationAirportCode,
    dispatch,
    enableSabreContent,
    duration,
    fareClass,
    isCharterBundle,
    isChoosingFilters,
    offer,
    prefetchFlights,
    requestBundledLuxPlusPricing,
    returnDate,
    rooms,
    shouldSearchFlights,
    triggerActivation,
  ])
}
